/**
 * Host Global - Property Rental Management System
 * Route Configuration
 *
 * Routes are organized by user role and permissions.
 * Each role sees only the routes they have access to.
 */

// Layouts
import Dashboard from "layouts/dashboard";
import SuperAdminDashboard from "layouts/super-admin/dashboard";
import SuperAdminInspections from "layouts/super-admin/inspections";
import InspectionReview from "layouts/super-admin/inspections/review";
import SuperAdminPropertyOwners from "layouts/super-admin/property-owners";
import SuperAdminProperties from "layouts/super-admin/properties";
import SuperAdminPackages from "layouts/super-admin/packages";
import SuperAdminUsers from "layouts/super-admin/users";
import ReservationsManagement from "layouts/super-admin/reservations";
import AmenitiesCriteriaManagement from "layouts/super-admin/amenities-criteria";
import CreateUser from "layouts/super-admin/users/create";
import Profile from "layouts/profile";
import SignIn from "layouts/authentication/sign-in";
import SignUp from "layouts/authentication/sign-up";
import MyProperties from "layouts/property-owner/my-properties";
import AddProperty from "layouts/property-owner/add-property";
import ReservationsCalendar from "layouts/property-owner/reservations-calendar";
import WalletDashboard from "layouts/property-owner/wallet";
import ReservationDetails from "layouts/property-owner/reservations-calendar/details";
import ClosureRequests from "layouts/property-owner/closure-requests";
import AdminProperties from "layouts/admin/properties";
import PropertyDetails from "layouts/admin/properties/details";
import AssignInspector from "layouts/admin/properties/assign-inspector";
import PromoCodesManagement from "layouts/admin/promo-codes";
import InspectorDashboard from "layouts/operations/inspections";
import AdminDashboard from "layouts/admin/dashboard";
import PropertyOwners from "layouts/admin/property-owners";
import OperationsTeam from "layouts/admin/operations-team";
import InspectorAnalytics from "layouts/admin/operations-team/inspector-analytics";
import AgentAnalytics from "layouts/admin/operations-team/agent-analytics";
import Inspections from "layouts/admin/inspections";
import CheckInOutDashboard from "layouts/checkin-out";
import CheckInOutReservationDetails from "layouts/checkin-out/details";
import HousekeepingDashboard from "layouts/housekeeping";
import ClosureRequestsManagement from "layouts/admin/closure-requests";
import AdminCallCenter from "layouts/admin/call-center";
import ItemsToProcess from "layouts/admin/items-to-process";
import EarlyCheckoutManagement from "layouts/super-admin/early-checkout";
import SuperAdminCallCenter from "layouts/super-admin/call-center";
import CallCenterDashboard from "layouts/call-center/dashboard";

// @mui icons
import Icon from "@mui/material/Icon";

// Super Admin Routes
const superAdminRoutes = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "super-admin-dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/super-admin/dashboard",
    component: <SuperAdminDashboard />,
  },
  {
    type: "collapse",
    name: "Property Owners",
    key: "super-admin-property-owners",
    icon: <Icon fontSize="small">people</Icon>,
    route: "/super-admin/property-owners",
    component: <SuperAdminPropertyOwners />,
  },
  {
    type: "collapse",
    name: "Properties",
    key: "super-admin-properties",
    icon: <Icon fontSize="small">home</Icon>,
    route: "/super-admin/properties",
    component: <SuperAdminProperties />,
  },
  {
    type: "collapse",
    name: "Packages",
    key: "super-admin-packages",
    icon: <Icon fontSize="small">inventory</Icon>,
    route: "/super-admin/packages",
    component: <SuperAdminPackages />,
  },
  {
    type: "collapse",
    name: "Users",
    key: "super-admin-users",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/super-admin/users",
    component: <SuperAdminUsers />,
  },
  {
    type: "collapse",
    name: "Inspections Review",
    key: "super-admin-inspections",
    icon: <Icon fontSize="small">task_alt</Icon>,
    route: "/super-admin/inspections",
    component: <SuperAdminInspections />,
  },
  {
    type: "collapse",
    name: "Promo Codes",
    key: "super-admin-promo-codes",
    icon: <Icon fontSize="small">local_offer</Icon>,
    route: "/admin/promo-codes",
    component: <PromoCodesManagement />,
  },
  {
    type: "collapse",
    name: "Reservations",
    key: "super-admin-reservations",
    icon: <Icon fontSize="small">calendar_today</Icon>,
    route: "/super-admin/reservations",
    component: <ReservationsManagement />,
  },
  {
    type: "collapse",
    name: "Items to Replace",
    key: "super-admin-items-to-process",
    icon: <Icon fontSize="small">build</Icon>,
    route: "/super-admin/items-to-process",
    component: <ItemsToProcess />,
  },
  {
    type: "collapse",
    name: "Amenities & Criteria",
    key: "super-admin-amenities-criteria",
    icon: <Icon fontSize="small">settings</Icon>,
    route: "/super-admin/amenities-criteria",
    component: <AmenitiesCriteriaManagement />,
  },
  {
    type: "collapse",
    name: "Closure Requests",
    key: "super-admin-closure-requests",
    icon: <Icon fontSize="small">event_busy</Icon>,
    route: "/super-admin/closure-requests",
    component: <ClosureRequestsManagement />,
  },
  {
    type: "collapse",
    name: "Stay Modifications",
    key: "super-admin-early-checkout",
    icon: <Icon fontSize="small">schedule</Icon>,
    route: "/super-admin/early-checkout",
    component: <EarlyCheckoutManagement />,
  },
  {
    type: "collapse",
    name: "Call Center",
    key: "super-admin-call-center",
    icon: <Icon fontSize="small">headset_mic</Icon>,
    route: "/super-admin/call-center",
    component: <SuperAdminCallCenter />,
  },
  {
    type: "route",
    name: "Profile",
    key: "profile",
    route: "/profile",
    component: <Profile />,
  },
];

// Admin Routes (Operation Manager - Property Management, Approvals, Task Assignment)
const adminRoutes = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/admin/dashboard",
    component: <AdminDashboard />,
  },
  {
    type: "collapse",
    name: "Properties",
    key: "properties",
    icon: <Icon fontSize="small">home</Icon>,
    route: "/admin/properties",
    component: <AdminProperties />,
  },
  {
    type: "collapse",
    name: "Property Owners",
    key: "property-owners",
    icon: <Icon fontSize="small">people</Icon>,
    route: "/admin/property-owners",
    component: <PropertyOwners />,
  },
  {
    type: "collapse",
    name: "Operations Team",
    key: "operations-team",
    icon: <Icon fontSize="small">assignment_ind</Icon>,
    route: "/admin/operations-team",
    component: <OperationsTeam />,
  },
  {
    type: "collapse",
    name: "Inspections",
    key: "inspections",
    icon: <Icon fontSize="small">search</Icon>,
    route: "/admin/inspections",
    component: <Inspections />,
  },
  {
    type: "collapse",
    name: "Analytics & Reports",
    key: "analytics",
    icon: <Icon fontSize="small">analytics</Icon>,
    route: "/admin/analytics",
    component: <Dashboard />, // TODO: Create Analytics & Reports page
  },
  {
    type: "collapse",
    name: "Housekeeping",
    key: "housekeeping",
    icon: <Icon fontSize="small">cleaning_services</Icon>,
    route: "/admin/housekeeping",
    component: <HousekeepingDashboard />,
  },
  {
    type: "collapse",
    name: "Items to Replace",
    key: "admin-items-to-process",
    icon: <Icon fontSize="small">build</Icon>,
    route: "/admin/items-to-process",
    component: <ItemsToProcess />,
  },
  {
    type: "collapse",
    name: "Closure Requests",
    key: "closure-requests",
    icon: <Icon fontSize="small">event_busy</Icon>,
    route: "/admin/closure-requests",
    component: <ClosureRequestsManagement />,
  },
  {
    type: "collapse",
    name: "Call Center",
    key: "admin-call-center",
    icon: <Icon fontSize="small">headset_mic</Icon>,
    route: "/admin/call-center",
    component: <AdminCallCenter />,
  },
  {
    type: "route",
    name: "Profile",
    key: "profile",
    route: "/profile",
    component: <Profile />,
  },
];

// Operations Team Routes (Inspections, Check-in/Check-out)
const operationsRoutes = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: <Dashboard />,
  },
  {
    type: "collapse",
    name: "Inspections",
    key: "inspections",
    icon: <Icon fontSize="small">search</Icon>,
    route: "/operations/inspections",
    component: <InspectorDashboard />,
  },
  {
    type: "route",
    name: "Profile",
    key: "profile",
    route: "/profile",
    component: <Profile />,
  },
];

// Financial Team Routes
const financialRoutes = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: <Dashboard />,
  },
  {
    type: "collapse",
    name: "Finances",
    key: "finances",
    icon: <Icon fontSize="small">account_balance</Icon>,
    route: "/financial/finances",
    component: <Dashboard />, // TODO: Create Finances page
  },
  {
    type: "collapse",
    name: "Payouts",
    key: "payouts",
    icon: <Icon fontSize="small">payments</Icon>,
    route: "/financial/payouts",
    component: <Dashboard />, // TODO: Create Payouts page
  },
  {
    type: "route",
    name: "Profile",
    key: "profile",
    route: "/profile",
    component: <Profile />,
  },
];

// Booking Agent Routes
const bookingAgentRoutes = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: <Dashboard />,
  },
  {
    type: "collapse",
    name: "Bookings",
    key: "bookings",
    icon: <Icon fontSize="small">calendar_today</Icon>,
    route: "/bookings",
    component: <Dashboard />, // TODO: Create Bookings page
  },
  {
    type: "route",
    name: "Profile",
    key: "profile",
    route: "/profile",
    component: <Profile />,
  },
];

// Call Center Agent Routes
const callCenterRoutes = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/call-center/dashboard",
    component: <CallCenterDashboard />,
  },
  {
    type: "route",
    name: "Profile",
    key: "profile",
    route: "/profile",
    component: <Profile />,
  },
];

// Check-In/Check-Out Agent Routes
const checkInOutRoutes = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: <Dashboard />,
  },
  {
    type: "collapse",
    name: "Check-In/Out",
    key: "checkin-out",
    icon: <Icon fontSize="small">event</Icon>,
    route: "/checkin-out",
    component: <CheckInOutDashboard />,
  },
  {
    type: "route",
    name: "Reservation Report",
    key: "checkin-out-reservation-details",
    route: "/checkin-out/reservation/:id",
    component: <CheckInOutReservationDetails />,
  },
  {
    type: "route",
    name: "Profile",
    key: "profile",
    route: "/profile",
    component: <Profile />,
  },
];

// Housekeeping Routes
const housekeepingRoutes = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: <Dashboard />,
  },
  {
    type: "collapse",
    name: "Cleaning Tasks",
    key: "cleaning-tasks",
    icon: <Icon fontSize="small">cleaning_services</Icon>,
    route: "/housekeeping/tasks",
    component: <HousekeepingDashboard />,
  },
  {
    type: "route",
    name: "Profile",
    key: "profile",
    route: "/profile",
    component: <Profile />,
  },
];

// Marketing Team Routes
const marketingRoutes = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: <Dashboard />,
  },
  {
    type: "collapse",
    name: "Campaigns",
    key: "campaigns",
    icon: <Icon fontSize="small">campaign</Icon>,
    route: "/marketing/campaigns",
    component: <Dashboard />, // TODO: Create Campaigns page
  },
  {
    type: "route",
    name: "Profile",
    key: "profile",
    route: "/profile",
    component: <Profile />,
  },
];

// Property Owner Routes
const propertyOwnerRoutes = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: <Dashboard />,
  },
  {
    type: "collapse",
    name: "My Properties",
    key: "my-properties",
    icon: <Icon fontSize="small">home</Icon>,
    route: "/property-owner/properties",
    component: <MyProperties />,
    collapse: [
      {
        name: "Add Property",
        key: "add-property",
        route: "/property-owner/properties/add",
        component: <AddProperty />,
      },
    ],
  },
  {
    type: "collapse",
    name: "Reservations",
    key: "reservations",
    icon: <Icon fontSize="small">event</Icon>,
    route: "/property-owner/reservations",
    component: <ReservationsCalendar />,
  },
  // Hidden route (details page)
  {
    type: "route",
    name: "Reservation Details",
    key: "reservation-details",
    route: "/property-owner/reservations/:id",
    component: <ReservationDetails />,
  },
  {
    type: "collapse",
    name: "Wallet",
    key: "wallet",
    icon: <Icon fontSize="small">account_balance_wallet</Icon>,
    route: "/property-owner/wallet",
    component: <WalletDashboard />,
  },
  {
    type: "collapse",
    name: "Closure Requests",
    key: "closure-requests",
    icon: <Icon fontSize="small">event_busy</Icon>,
    route: "/property-owner/closure-requests",
    component: <ClosureRequests />,
  },
  {
    type: "route",
    name: "Profile",
    key: "profile",
    route: "/profile",
    component: <Profile />,
  },
];

// Authentication Routes
const authRoutes = [
  {
    type: "collapse",
    name: "Sign In",
    key: "sign-in",
    icon: <Icon fontSize="small">login</Icon>,
    route: "/authentication/sign-in",
    component: <SignIn />,
  },
  {
    type: "collapse",
    name: "Sign Up",
    key: "sign-up",
    icon: <Icon fontSize="small">assignment</Icon>,
    route: "/authentication/sign-up",
    component: <SignUp />,
  },
];

// Get routes based on user role
// This function returns role-specific routes for the admin dashboard
export const getRoutes = (userRole) => {
  // Authentication routes are not included in sidebar (they're accessible via direct URL only)
  const baseRoutes = [];

  // Return role-specific routes
  switch (userRole) {
    case "Super_Admin":
      return [...superAdminRoutes, ...baseRoutes];
    case "Admin":
      return [...adminRoutes, ...baseRoutes];
    case "Operations":
      return [...operationsRoutes, ...baseRoutes];
    case "Financial Team":
      return [...financialRoutes, ...baseRoutes];
    case "Booking Agent":
      return [...bookingAgentRoutes, ...baseRoutes];
    case "Check-In/Check-Out Agent":
      return [...checkInOutRoutes, ...baseRoutes];
    case "Housekeeping":
      return [...housekeepingRoutes, ...baseRoutes];
    case "Call_Center_Agent":
      return [...callCenterRoutes, ...baseRoutes];
    case "Marketing Team":
      return [...marketingRoutes, ...baseRoutes];
    case "Property_Owner":
    case "Property Owner":
      return [...propertyOwnerRoutes, ...baseRoutes];
    default:
      // Default dashboard for authenticated users without specific role
      return [
        {
          type: "collapse",
          name: "Dashboard",
          key: "dashboard",
          icon: <Icon fontSize="small">dashboard</Icon>,
          route: "/dashboard",
          component: <Dashboard />,
        },
        ...baseRoutes,
      ];
  }
};

// Default routes (for backward compatibility - used when user is not authenticated)
const routes = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: <Dashboard />,
  },
  ...authRoutes,
];

export default routes;
